"use client";

import {
  BadgePercent,
  Coffee,
  Lightbulb,
  Loader2,
  MessageSquareText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/locales/client";

interface OfferStepProps {
  reason: string;
  onClaimOffer: () => void;
  onContinueCanceling: () => void;
  isClaimingOffer: boolean;
  isCanceling: boolean;
  planAmount: number | null;
  planInterval: "month" | "year" | null;
}

export const OfferStep = ({
  reason,
  onClaimOffer,
  onContinueCanceling,
  isClaimingOffer,
  isCanceling,
  planAmount,
  planInterval,
}: OfferStepProps) => {
  switch (reason) {
    case "too_expensive":
    case "other":
      return (
        <DiscountOffer
          onClaimOffer={onClaimOffer}
          onContinueCanceling={onContinueCanceling}
          isClaimingOffer={isClaimingOffer}
          isCanceling={isCanceling}
          planAmount={planAmount}
          planInterval={planInterval}
        />
      );
    case "discount_reminder":
      return (
        <DiscountReminderOffer
          onClaimOffer={onClaimOffer}
          onContinueCanceling={onContinueCanceling}
          isCanceling={isCanceling}
        />
      );
    case "no_longer_need":
      return (
        <PauseOffer
          onClaimOffer={onClaimOffer}
          onContinueCanceling={onContinueCanceling}
          isClaimingOffer={isClaimingOffer}
          isCanceling={isCanceling}
        />
      );
    case "need_more_features":
      return (
        <FeatureRequestOffer
          onClaimOffer={onClaimOffer}
          onContinueCanceling={onContinueCanceling}
          isCanceling={isCanceling}
        />
      );
    case "quality_less_than_expected":
      return (
        <QualityOffer
          onClaimOffer={onClaimOffer}
          onContinueCanceling={onContinueCanceling}
          isCanceling={isCanceling}
        />
      );
    default:
      return (
        <DiscountOffer
          onClaimOffer={onClaimOffer}
          onContinueCanceling={onContinueCanceling}
          isClaimingOffer={isClaimingOffer}
          isCanceling={isCanceling}
          planAmount={planAmount}
          planInterval={planInterval}
        />
      );
  }
};

interface BaseOfferProps {
  onClaimOffer: () => void;
  onContinueCanceling: () => void;
  isCanceling: boolean;
}

interface LoadableOfferProps extends BaseOfferProps {
  isClaimingOffer: boolean;
}

interface DiscountOfferProps extends LoadableOfferProps {
  planAmount: number | null;
  planInterval: "month" | "year" | null;
}

const DISCOUNT_PERCENT = 30;

const OfferActions = ({
  ctaText,
  onClaimOffer,
  onContinueCanceling,
  isClaimingOffer,
  isCanceling,
}: {
  ctaText: string;
  onClaimOffer: () => void;
  onContinueCanceling: () => void;
  isClaimingOffer: boolean;
  isCanceling: boolean;
}) => {
  const t = useI18n();
  const isLoading = isClaimingOffer || isCanceling;

  return (
    <div className="flex flex-col gap-2 w-full">
      <Button onClick={onClaimOffer} disabled={isLoading} className="w-full">
        {isClaimingOffer && <Loader2 className="mr-2 size-4 animate-spin" />}
        {ctaText}
      </Button>
      <Button
        onClick={onContinueCanceling}
        disabled={isLoading}
        className="w-full hover:text-destructive/80 border border-red-200 bg-white text-destructive shadow-sm hover:bg-slate-100"
      >
        {isCanceling && <Loader2 className="mr-2 size-4 animate-spin" />}
        {t("cancellation.offers.continueCanceling")}
      </Button>
    </div>
  );
};

const OfferBadge = ({ text }: { text: string }) => (
  <span className="rounded-full bg-brand-primary/10 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-brand-primary">
    {text}
  </span>
);

const DiscountOffer = ({
  onClaimOffer,
  onContinueCanceling,
  isClaimingOffer,
  isCanceling,
  planAmount,
  planInterval,
}: DiscountOfferProps) => {
  const t = useI18n();

  const originalPrice = planAmount ?? 14.99;
  const discountedPrice = (
    originalPrice *
    (1 - DISCOUNT_PERCENT / 100)
  ).toFixed(2);
  const intervalLabel =
    planInterval === "year"
      ? t("cancellation.offers.discount.year")
      : t("cancellation.offers.discount.month");

  // @ts-expect-error
  const badgeText = t("cancellation.offers.discount.badge", {
    percent: DISCOUNT_PERCENT,
    interval: intervalLabel,
  });

  return (
    <div className="flex flex-col items-center gap-6">
      <div className="flex flex-col items-center gap-4 rounded-xl bg-muted/50 px-8 py-6 w-full">
        <OfferBadge text={badgeText} />
        <div className="flex items-baseline gap-2">
          <span className="text-lg text-muted-foreground line-through">
            ${originalPrice.toFixed(2)}
          </span>
          <span className="text-sm font-medium text-muted-foreground uppercase">
            {t("cancellation.offers.discount.only")}
          </span>
          <span className="text-3xl font-bold text-foreground">
            ${discountedPrice}
          </span>
        </div>
      </div>
      <OfferActions
        ctaText={t("cancellation.offers.discount.cta")}
        onClaimOffer={onClaimOffer}
        onContinueCanceling={onContinueCanceling}
        isClaimingOffer={isClaimingOffer}
        isCanceling={isCanceling}
      />
    </div>
  );
};

const PauseOffer = ({
  onClaimOffer,
  onContinueCanceling,
  isClaimingOffer,
  isCanceling,
}: LoadableOfferProps) => {
  const t = useI18n();

  return (
    <div className="flex flex-col items-center gap-6">
      <div className="flex flex-col items-center gap-4 rounded-xl bg-muted/50 px-8 py-6 w-full">
        <OfferBadge text={t("cancellation.offers.pause.badge")} />
        <Coffee className="size-10 text-brand-primary" />
        <p className="text-sm text-center text-muted-foreground">
          {t("cancellation.description.offerPause")}
        </p>
      </div>
      <OfferActions
        ctaText={t("cancellation.offers.pause.cta")}
        onClaimOffer={onClaimOffer}
        onContinueCanceling={onContinueCanceling}
        isClaimingOffer={isClaimingOffer}
        isCanceling={isCanceling}
      />
    </div>
  );
};

const FeatureRequestOffer = ({
  onClaimOffer,
  onContinueCanceling,
  isCanceling,
}: BaseOfferProps) => {
  const t = useI18n();

  return (
    <div className="flex flex-col items-center gap-6">
      <div className="flex flex-col items-center gap-4 rounded-xl bg-muted/50 px-8 py-6 w-full">
        <OfferBadge text={t("cancellation.offers.features.badge")} />
        <Lightbulb className="size-10 text-brand-primary" />
        <p className="text-sm text-center text-muted-foreground">
          {t("cancellation.description.offerFeatures")}
        </p>
      </div>
      <OfferActions
        ctaText={t("cancellation.offers.features.cta")}
        onClaimOffer={onClaimOffer}
        onContinueCanceling={onContinueCanceling}
        isClaimingOffer={false}
        isCanceling={isCanceling}
      />
    </div>
  );
};

const QualityOffer = ({
  onClaimOffer,
  onContinueCanceling,
  isCanceling,
}: BaseOfferProps) => {
  const t = useI18n();

  return (
    <div className="flex flex-col items-center gap-6">
      <div className="flex flex-col items-center gap-4 rounded-xl bg-muted/50 px-8 py-6 w-full">
        <OfferBadge text={t("cancellation.offers.quality.badge")} />
        <MessageSquareText className="size-10 text-brand-primary" />
        <p className="text-sm text-center text-muted-foreground">
          {t("cancellation.description.offerQuality")}
        </p>
      </div>
      <OfferActions
        ctaText={t("cancellation.offers.quality.cta")}
        onClaimOffer={onClaimOffer}
        onContinueCanceling={onContinueCanceling}
        isClaimingOffer={false}
        isCanceling={isCanceling}
      />
    </div>
  );
};

const DiscountReminderOffer = ({
  onClaimOffer,
  onContinueCanceling,
  isCanceling,
}: BaseOfferProps) => {
  const t = useI18n();

  return (
    <div className="flex flex-col items-center gap-6">
      <div className="flex flex-col items-center gap-4 rounded-xl bg-muted/50 px-8 py-6 w-full">
        <OfferBadge text={t("cancellation.offers.discountReminder.badge")} />
        <BadgePercent className="size-10 text-brand-primary" />
        <p className="text-sm text-center text-muted-foreground">
          {t("cancellation.description.offerDiscountReminder")}
        </p>
      </div>
      <OfferActions
        ctaText={t("cancellation.offers.discountReminder.cta")}
        onClaimOffer={onClaimOffer}
        onContinueCanceling={onContinueCanceling}
        isClaimingOffer={false}
        isCanceling={isCanceling}
      />
    </div>
  );
};
